/**
 * Shared types for the scanner API (/scanner/v2).
 */
import { isRollingDateValue } from '@/features/sliced-form/schemas/filter-entity'
import { toHhMmSs } from '@/components/ui/time-input-utils'
import {
  resolveRollingDate,
  resolveRollingRange,
} from '@/features/sliced-form/utils/resolve-rolling-dates'

export interface ScannerRow {
  ticker: string
  index?: number | null
  session_chg_p?: number | null
  session_chg?: number | null
  session_lp?: number | null
  session_v?: number | null
  rvol?: number | null
  dollar_vol?: number | null
  trades?: number | null
  pm_chg_p?: number | null
  pm_chg?: number | null
  pm_v?: number | null
  session_hi?: number | null
  session_lo?: number | null
  session_op?: number | null
  marketcap?: number | null
  sharesfloat?: number | null
  halt?: boolean | null
  ssr?: boolean | null
  sector?: string | null
  industry?: string | null
  exchange?: string | null
  /** Frontend-only: custom RVOL vs the user's reference days. Computed on the
   *  client in the scanner datasource, never returned by the API. */
  custom_rvol?: number | null
}

export interface ScannerFilter {
  column: string
  operator: string
  value: unknown
}

// ─── Nested filter types (matches edge backend /scanner/v2 format) ───

/** A single filter condition with left/right binary args. */
export interface QueryFilterNode {
  left: { column: string; expression?: string; args?: unknown; label?: string }
  operator: string
  right:
    | { value: unknown }
    | { column: string; expression?: string; args?: unknown; label?: string }
  allowNull?: boolean
}

/** A group of filters joined by AND or OR. */
export interface QueryFilterGroup {
  AND?: QueryFilterTree[]
  OR?: QueryFilterTree[]
}

/** Recursive union: either a leaf node or a nested group. */
export type QueryFilterTree = QueryFilterNode | QueryFilterGroup

export interface ScannerRequestBody {
  columns: string[]
  filters: QueryFilterGroup
  sort_field: string
  sort_dir: 'asc' | 'desc'
}

// ─── Backend request transformation ───

/** Maps frontend operator strings to the backend's expected format. */
export const OPERATOR_TO_BACKEND: Record<string, string> = {
  gt: 'GT',
  gte: 'GE',
  lt: 'LT',
  lte: 'LE',
  eq: 'EQ',
  neq: 'NE',
  between: 'BT',
  in: 'IN',
  not_in: 'NIN',
  contains: 'CONTAINS',
  not_contains: 'NOT_CONTAINS',
  is_true: 'EQ',
  is_false: 'EQ',
}

const BACKEND_OPERATORS: ReadonlySet<string> = new Set(Object.values(OPERATOR_TO_BACKEND))

/** Same suffix rule as the history catalog's `string_time` inference. */
function isTimeOfDayKey(key: string | undefined): boolean {
  return !!key && (key.endsWith('_time') || key.endsWith('_halt'))
}

/** Recursively count leaf filter nodes in a QueryFilterGroup tree. */
export function countFilterLeaves(group: QueryFilterGroup): number {
  let count = 0
  for (const key of ['AND', 'OR'] as const) {
    const children = group[key]
    if (!children) continue
    for (const child of children) {
      if ('AND' in child || 'OR' in child) {
        count += countFilterLeaves(child as QueryFilterGroup)
      } else {
        count += 1
      }
    }
  }
  return count
}

function isFilterGroup(node: QueryFilterTree): node is QueryFilterGroup {
  return 'AND' in node || 'OR' in node
}

/** Backend operators that take one value; a list on these is a 400. */
const SCALAR_BACKEND_OPERATORS: ReadonlySet<string> = new Set(['GT', 'GE', 'LT', 'LE', 'EQ', 'NE'])

/**
 * Recursively walks a QueryFilterGroup tree, mapping each leaf node's operator
 * to the backend format. Leaves whose operator isn't in the mapping are dropped.
 * `is_true` / `is_false` get their right-hand side replaced with `{ value: true/false }`.
 */
export function transformFiltersForBackend(group: QueryFilterGroup): QueryFilterGroup {
  const result: QueryFilterGroup = {}

  for (const key of ['AND', 'OR'] as const) {
    const children = group[key]
    if (!children) continue

    result[key] = children
      .map((child): QueryFilterTree | null => {
        if (isFilterGroup(child)) {
          return transformFiltersForBackend(child)
        }

        // Idempotent: a leaf that ALREADY carries a backend token passes through.
        // Deep Research embeds panels with canonical tokens (its journal stores
        // them that way); dropping those made every embedded chart query the whole
        // universe with no error (found 2026-09-17). Unknown operators still drop.
        const backendOp =
          OPERATOR_TO_BACKEND[child.operator] ??
          (BACKEND_OPERATORS.has(child.operator) ? child.operator : undefined)
        if (!backendOp) return null

        // Defensive: a leaf without binary left/right args isn't query-shaped
        // (e.g. a profile-format leaf {column, operator, value} applied to page
        // filters without translation). Drop it instead of throwing below —
        // one malformed leaf must not take down every panel's requests.
        if (child.right == null || typeof child.right !== 'object') {
          console.warn('[filters] dropping non-query-shaped filter leaf', child)
          return null
        }

        const transformed = { ...child, operator: backendOp }

        if (child.operator === 'is_true') {
          transformed.right = { value: true }
        } else if (child.operator === 'is_false') {
          transformed.right = { value: false }
        }

        // Resolve rolling date values to concrete dates. Replace `right` with a
        // copy rather than writing through it — after the shallow spread above,
        // `transformed.right` still aliases the caller's object, which may be
        // frozen (filters stored by RTK Query are deep-frozen in dev).
        if ('value' in transformed.right) {
          const rv = (transformed.right as { value: unknown }).value
          if (isRollingDateValue(rv)) {
            transformed.right = { ...transformed.right, value: resolveRollingDate(rv) }
          } else if (Array.isArray(rv) && rv.length === 2) {
            transformed.right = {
              ...transformed.right,
              value: resolveRollingRange(rv[0], rv[1]),
            }
          }
        }

        // A scalar operator with a [from, to] list is refused (400). History page
        // filters are stored query-shaped, so a leaf saved that way (the date
        // picker always emits a range) never passes back through profileToQuery
        // and failed on every load. Send the start, as profileToQuery now does.
        if ('value' in transformed.right && SCALAR_BACKEND_OPERATORS.has(backendOp)) {
          const sv = (transformed.right as { value: unknown }).value
          if (Array.isArray(sv) && sv.length === 2) {
            transformed.right = { ...transformed.right, value: sv[0] }
          }
        }

        // Time-of-day columns: the stored `HH:mm` must reach the backend as
        // `HH:mm:ss` (MotherDuck cannot decode `15:00` into TIME). profileToQuery
        // already does this for freshly built trees; this covers persisted
        // query-shape filters and callers that never went through it.
        if ('value' in transformed.right && isTimeOfDayKey(child.left?.column)) {
          const tv = (transformed.right as { value: unknown }).value
          transformed.right = {
            ...transformed.right,
            value: Array.isArray(tv) ? tv.map(toHhMmSs) : toHhMmSs(tv),
          }
        }

        return transformed
      })
      .filter((n): n is QueryFilterTree => n !== null)
  }

  return result
}
